/**
 * Hook de Observabilidade
 * 
 * Fornece dados para o dashboard de observabilidade:
 * - Estatísticas gerais (taxa de sucesso, latência média)
 * - Logs de requisições com filtros
 * - Métricas por provider
 * - Volume temporal de requisições
 * 
 * IMPORTANTE: Usa RPCs para agregação no banco de dados,
 * evitando limite de 1000 registros do Supabase.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

// ============= Interfaces =============

/**
 * Estatísticas gerais de observabilidade
 */
export interface ObservabilityStats {
  totalRequests24h: number;
  successRate: number;
  errorRate: number;
  avgLatencyMs: number;
  inboundCount: number;
  outboundCount: number;
  byProvider: { provider: string; count: number; successRate: number; avgLatencyMs: number }[];
  bySource: { source: string; count: number }[];
}

/**
 * Registro de log de requisição API
 */
export interface ApiRequestLog {
  id: string;
  tenant_id: string | null;
  inbox_id: string | null;
  provider_slug: string;
  source_type: string;
  source_id: string | null;
  operation_type: string;
  direction: string;
  endpoint: string | null;
  request_payload: Record<string, unknown> | null;
  request_headers: Record<string, unknown> | null;
  response_status: number | null;
  response_payload: Record<string, unknown> | null;
  response_time_ms: number | null;
  status: string;
  error_message: string | null;
  error_code: string | null;
  message_id: string | null;
  conversation_id: string | null;
  contact_id: string | null;
  campaign_id: string | null;
  flow_id: string | null;
  flow_execution_id: string | null;
  external_id: string | null;
  correlation_id: string | null;
  ip_address: string | null;
  created_at: string;
  completed_at: string | null;
  // Campos enriquecidos
  tenant_name?: string;
  inbox_name?: string;
}

/**
 * Dados de volume por hora
 */
export interface VolumeDataPoint {
  hour: string;
  success: number;
  error: number;
  total: number;
}

/**
 * Dados de latência por provider
 */
export interface LatencyDataPoint {
  provider: string;
  avgLatency: number;
  p95Latency: number;
  maxLatency: number;
}

/**
 * Filtros para busca de logs
 */
export interface ObservabilityFilters {
  provider?: string;
  status?: string;
  source?: string;
  direction?: string;
  tenantId?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
  phone?: string; // Filtro por telefone no request_payload
}

/**
 * Interface para itens de requisição mais lentos
 * Utilizada pelo KPI Top 20 na página de Observabilidade
 */
export interface SlowestApiRequest {
  id: string;
  tenant_id: string | null;
  tenant_name?: string;
  provider_slug: string;
  source_type: string;
  operation_type: string;
  direction: string;
  endpoint: string | null;
  response_status: number | null;
  response_time_ms: number;
  response_time_formatted: string;
  status: string;
  error_message: string | null;
  request_payload: Record<string, unknown> | null;
  response_payload: Record<string, unknown> | null;
  created_at: string;
  completed_at: string | null;
}

// ============= Helpers =============

/**
 * Formata tempo de resposta em milissegundos para exibição amigável
 * Exemplos: 500ms, 2.5s, 1m 30s
 */
function formatResponseTimeMs(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}m ${remainingSeconds}s`;
}

// ============= Hooks =============

/**
 * Hook para buscar estatísticas gerais de observabilidade
 * Usa RPCs para agregação no banco, evitando limite de 1000 registros
 */
export function useObservabilityStats(hours: number = 24) {
  return useQuery({
    queryKey: ['observability-stats', hours],
    queryFn: async () => {
      // Chamar as 3 RPCs em paralelo
      const [statsResult, providerResult, sourceResult] = await Promise.all([
        supabase.rpc('get_observability_stats', { hours_ago: hours }),
        supabase.rpc('get_observability_by_provider', { hours_ago: hours }),
        supabase.rpc('get_observability_by_source', { hours_ago: hours }),
      ]);

      if (statsResult.error) throw statsResult.error;
      if (providerResult.error) throw providerResult.error;
      if (sourceResult.error) throw sourceResult.error;

      // Processar resultado das estatísticas gerais
      const statsData = statsResult.data?.[0] || {
        total_requests: 0,
        success_count: 0,
        error_count: 0,
        inbound_count: 0,
        outbound_count: 0,
        avg_latency_ms: 0,
      };

      const total = Number(statsData.total_requests) || 0;
      const success = Number(statsData.success_count) || 0;
      const errors = Number(statsData.error_count) || 0;

      // Processar dados por provider
      const byProvider = (providerResult.data || []).map((p: {
        provider: string;
        total_count: number;
        success_count: number;
        avg_latency_ms: number;
      }) => ({
        provider: p.provider,
        count: Number(p.total_count) || 0,
        successRate: p.total_count > 0 ? (Number(p.success_count) / Number(p.total_count)) * 100 : 0,
        avgLatencyMs: Number(p.avg_latency_ms) || 0,
      }));

      // Processar dados por source
      const bySource = (sourceResult.data || []).map((s: { source: string; count: number }) => ({
        source: s.source,
        count: Number(s.count) || 0,
      }));

      const stats: ObservabilityStats = {
        totalRequests24h: total,
        successRate: total > 0 ? (success / total) * 100 : 100,
        errorRate: total > 0 ? (errors / total) * 100 : 0,
        avgLatencyMs: Number(statsData.avg_latency_ms) || 0,
        inboundCount: Number(statsData.inbound_count) || 0,
        outboundCount: Number(statsData.outbound_count) || 0,
        byProvider,
        bySource,
      };

      return stats;
    },
    refetchInterval: 30000, // Atualizar a cada 30 segundos
  });
}

/**
 * Hook para buscar logs de requisições com filtros e paginação
 * Mantém query direta pois já usa paginação
 */
export function useApiRequestLogs(
  filters: ObservabilityFilters = {},
  page: number = 1,
  pageSize: number = 50
) {
  return useQuery({
    queryKey: ['api-request-logs', filters, page, pageSize],
    queryFn: async () => {
      // Período padrão: últimas 24h
      const startDate = filters.startDate || new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const endDate = filters.endDate || new Date().toISOString();

      let query = supabase
        .from('api_request_logs')
        .select('*', { count: 'exact' })
        .gte('created_at', startDate)
        .lte('created_at', endDate)
        .order('created_at', { ascending: false })
        .range((page - 1) * pageSize, page * pageSize - 1);

      // Aplicar filtros
      if (filters.provider && filters.provider !== 'all') {
        query = query.eq('provider_slug', filters.provider);
      }
      if (filters.status && filters.status !== 'all') {
        query = query.eq('status', filters.status);
      }
      if (filters.source && filters.source !== 'all') {
        query = query.eq('source_type', filters.source);
      }
      if (filters.direction && filters.direction !== 'all') {
        query = query.eq('direction', filters.direction);
      }
      if (filters.tenantId && filters.tenantId !== 'all') {
        query = query.eq('tenant_id', filters.tenantId);
      }
      if (filters.search) {
        // Busca por error_message (external_id e correlation_id são UUID, não suportam ILIKE)
        query = query.ilike('error_message', `%${filters.search}%`);
      }

      // Filtro por telefone no request_payload
      if (filters.phone) {
        const phoneDigits = filters.phone.replace(/\D/g, '');
        if (phoneDigits) {
          query = query.ilike('request_payload->>phone', `%${phoneDigits}%`);
        }
      }

      const { data, error, count } = await query;

      if (error) throw error;

      // Coletar IDs únicos de tenants para buscar nomes
      const uniqueTenantIds = [...new Set(
        (data || [])
          .map(log => log.tenant_id)
          .filter((id): id is string => id !== null)
      )];

      // Buscar nomes dos tenants em uma única query (otimizado)
      let tenantNamesMap: Record<string, string> = {};
      if (uniqueTenantIds.length > 0) {
        const { data: tenantsData } = await supabase
          .from('tenants')
          .select('id, name')
          .in('id', uniqueTenantIds);
        
        tenantNamesMap = (tenantsData || []).reduce((acc, t) => {
          acc[t.id] = t.name;
          return acc;
        }, {} as Record<string, string>);
      }

      // Enriquecer logs com tenant_name
      const enrichedLogs: ApiRequestLog[] = (data || []).map(log => ({
        ...log,
        request_payload: log.request_payload as Record<string, unknown> | null,
        request_headers: log.request_headers as Record<string, unknown> | null,
        response_payload: log.response_payload as Record<string, unknown> | null,
        tenant_name: log.tenant_id ? tenantNamesMap[log.tenant_id] : undefined,
      }));

      return {
        logs: enrichedLogs,
        total: count || 0,
        page,
        pageSize,
        totalPages: Math.ceil((count || 0) / pageSize),
      };
    },
    refetchInterval: 30000,
  });
}

/**
 * Hook para buscar volume de requisições por hora
 * Usa RPC para agregação no banco
 */
export function useRequestVolume(hours: number = 24) {
  return useQuery({
    queryKey: ['request-volume', hours],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_observability_volume_hourly', { hours_ago: hours });

      if (error) throw error;

      // Converter para formato esperado pelo componente
      const volumeData: VolumeDataPoint[] = (data || []).map((item: {
        hour_label: string;
        success_count: number;
        error_count: number;
      }) => ({
        hour: item.hour_label,
        success: Number(item.success_count) || 0,
        error: Number(item.error_count) || 0,
        total: (Number(item.success_count) || 0) + (Number(item.error_count) || 0),
      }));

      return volumeData;
    },
    refetchInterval: 60000,
  });
}

/**
 * Hook para buscar latência por provider
 * Usa RPC para agregação no banco (inclui P95 real calculado pelo PostgreSQL)
 */
export function useLatencyByProvider(hours: number = 24) {
  return useQuery({
    queryKey: ['latency-by-provider', hours],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_observability_by_provider', { hours_ago: hours });

      if (error) throw error;

      // Converter para formato esperado pelo componente
      const latencyData: LatencyDataPoint[] = (data || []).map((item: {
        provider: string;
        avg_latency_ms: number;
        p95_latency_ms: number;
      }) => ({
        provider: item.provider,
        avgLatency: Math.round(Number(item.avg_latency_ms) || 0),
        p95Latency: Math.round(Number(item.p95_latency_ms) || 0),
        maxLatency: 0, // Não mais necessário, usando P95 real
      }));

      return latencyData;
    },
    refetchInterval: 60000,
  });
}

/**
 * Hook para buscar lista de tenants para filtro
 */
export function useTenantsList() {
  return useQuery({
    queryKey: ['tenants-list'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tenants')
        .select('id, name')
        .order('name');

      if (error) throw error;
      return data || [];
    },
    staleTime: 5 * 60 * 1000, // Cache por 5 minutos
  });
}

/**
 * Hook para buscar as requisições API mais lentas das últimas 24 horas
 * Utilizado pelo KPI Top 20 na página de Observabilidade
 */
export function useSlowestApiRequests(limit: number = 20) {
  return useQuery({
    queryKey: ['api-requests-slowest', limit],
    queryFn: async () => {
      // Chamar RPC que retorna as requisições mais lentas
      const { data, error } = await supabase.rpc('get_api_request_logs_slowest', {
        p_limit: limit
      });
      
      if (error) throw error;
      if (!data || data.length === 0) return [];
      
      // Extrair tenant_ids únicos para enriquecimento com nomes
      const tenantIds = new Set<string>();
      data.forEach((item: { tenant_id: string | null }) => {
        if (item.tenant_id) tenantIds.add(item.tenant_id);
      });

      // Buscar nomes dos tenants em uma única query
      let tenantMap = new Map<string, string>();
      if (tenantIds.size > 0) {
        const { data: tenantsData } = await supabase
          .from('tenants')
          .select('id, name')
          .in('id', Array.from(tenantIds));
        tenantMap = new Map((tenantsData || []).map(t => [t.id, t.name]));
      }
      
      // Enriquecer dados com tenant_name e formatar tempo
      return data.map((item: {
        id: string;
        tenant_id: string | null;
        provider_slug: string;
        source_type: string;
        operation_type: string;
        direction: string;
        endpoint: string | null;
        response_status: number | null;
        response_time_ms: number | null;
        status: string;
        error_message: string | null;
        request_payload: unknown;
        response_payload: unknown;
        created_at: string;
        completed_at: string | null;
      }) => ({
        ...item,
        tenant_name: item.tenant_id ? tenantMap.get(item.tenant_id) || 'N/A' : 'Sistema',
        response_time_formatted: formatResponseTimeMs(item.response_time_ms || 0),
        request_payload: item.request_payload as Record<string, unknown> | null,
        response_payload: item.response_payload as Record<string, unknown> | null,
      })) as SlowestApiRequest[];
    },
    refetchInterval: 30000, // Atualizar a cada 30 segundos
  });
}
